import { useFreeTier } from '~/composables/useFreeTier';
import { useFreePlan } from '~/composables/useFreePlan';
import { canRunProductTour, shouldOpenPlansAfterTour } from '~/utils/experiment/productTour';

// titleKey/bodyKey are i18n keys (dashboard.freeTierTour.*), resolved with t() by FreeTierTour.client.vue.
// uaeBodyKey: copy for UAE agents (no "free" workspace; the full course is included).
export type TourStep = { route: string; selector: string; titleKey: string; bodyKey: string; uaeBodyKey?: string };

const STEPS: TourStep[] = [
  {
    route: '/dashboard',
    // UAE agents who finished the checklist: their setup panel instead.
    selector: '[data-tour="checklist"], [data-tour="uae-setup"]',
    titleKey: 'dashboard.freeTierTour.welcomeTitle',
    bodyKey: 'dashboard.freeTierTour.welcomeBody',
    uaeBodyKey: 'dashboard.freeTierTour.welcomeBodyUae',
  },
  {
    route: '/dashboard',
    selector: '[data-tour="news-feed"]',
    titleKey: 'dashboard.freeTierTour.feedTitle',
    bodyKey: 'dashboard.freeTierTour.feedBody',
  },
  {
    route: '/dashboard',
    selector: '[data-tour="nav-ask"]',
    titleKey: 'dashboard.freeTierTour.askTitle',
    bodyKey: 'dashboard.freeTierTour.askBody',
  },
  {
    route: '/ask',
    selector: '[data-tour="ask-composer"]',
    titleKey: 'dashboard.freeTierTour.tryAskTitle',
    bodyKey: 'dashboard.freeTierTour.tryAskBody',
  },
  {
    route: '/marketing',
    selector: '[data-tour="marketing-generate"]',
    titleKey: 'dashboard.freeTierTour.marketingTitle',
    bodyKey: 'dashboard.freeTierTour.marketingBody',
  },
  {
    route: '/learn',
    // Full-course viewers (Pro, UAE) have no free-lesson block: the course sidebar instead.
    selector: '[data-tour="free-lesson"], [data-tour="course"]',
    titleKey: 'dashboard.freeTierTour.masterclassTitle',
    bodyKey: 'dashboard.freeTierTour.masterclassBody',
    uaeBodyKey: 'dashboard.freeTierTour.masterclassBodyUae',
  },
];

const SEEN_KEY = 'freeTierTour:seen';

export const useFreeTierTour = () => {
  const { isFreeTier } = useFreeTier();
  const { isFreePlanUser } = useFreePlan();
  const { isUae } = useUaeGate();
  const eligible = computed(() => canRunProductTour({ isFreePlanUser: isFreePlanUser.value, isUae: isUae.value }));
  const { trackExperimentEvent } = useExperiment();
  const { openModal: openBillingModal } = useBillingModal();

  // useState survives SPA route changes (the tour navigates between pages).
  const active = useProductTourActive();
  const stepIndex = useState<number>('freeTierTour:step', () => 0);
  // Tracks whether the current run was initiated via replay() so we don't
  // re-open the pricing popup when the user replays the tour.
  const isReplaySession = useState<boolean>('freeTierTour:replay', () => false);

  const currentStep = computed<TourStep | null>(() => STEPS[stepIndex.value] ?? null);
  const isLast = computed(() => stepIndex.value >= STEPS.length - 1);

  const hasSeen = () => Boolean(import.meta.client) && localStorage.getItem(SEEN_KEY) === '1';
  const markSeen = () => {
    if (import.meta.client) localStorage.setItem(SEEN_KEY, '1');
  };

  const start = () => {
    if (!eligible.value || hasSeen() || active.value) return;
    isReplaySession.value = false;
    stepIndex.value = 0;
    active.value = true;
    trackExperimentEvent('tour_started');
  };

  const next = () => {
    if (isLast.value) return finish();
    stepIndex.value += 1;
    trackExperimentEvent('tour_step', { step: stepIndex.value + 1 });
  };

  const prev = () => {
    if (stepIndex.value > 0) stepIndex.value -= 1;
  };

  // Runs after active is cleared, so the popup is no longer blocked by the tour guard.
  const afterTour = () => {
    if (shouldOpenPlansAfterTour({ firstRun: !isReplaySession.value, isUae: isUae.value })) openBillingModal();
  };

  const finish = () => {
    active.value = false;
    markSeen();
    trackExperimentEvent('tour_completed');
    afterTour();
  };

  const skip = () => {
    if (!active.value) return;
    trackExperimentEvent('tour_skipped', { step: stepIndex.value + 1 });
    active.value = false;
    markSeen();
    afterTour();
  };

  // Replay clears the seen flag for this session and restarts from step 1.
  const replay = () => {
    if (!eligible.value) return; // defense-in-depth: free-plan INT users and UAE agents only
    if (import.meta.client) localStorage.removeItem(SEEN_KEY);
    isReplaySession.value = true;
    stepIndex.value = 0;
    active.value = true;
    trackExperimentEvent('tour_started');
  };

  return {
    isFreeTier,
    active,
    stepIndex,
    currentStep,
    isLast,
    steps: STEPS,
    start,
    next,
    prev,
    finish,
    skip,
    replay,
    hasSeen,
  };
};
